import {
    Color,
    FrontSide,
    Group,
    Material,
    Mesh,
    MeshLambertMaterial,
    MeshPhysicalMaterial,
    MeshStandardMaterial
} from "three";

import React, { memo, useEffect, useMemo, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { GLTF } from "three-stdlib";
import { useFrame } from "@react-three/fiber";
import { BOTTOM_OFFSET, DEFAULT_HEIGHT, TOP_OFFSET } from "../../../constants";
import { useStoreRoom, useStoreSystem } from "../../../store";
import { Utils } from "../../../utils/utils";

export type GLTFResult = GLTF & {
    nodes: {
        Cube005: Mesh;
        Cube005_1: Mesh;
        sideWalls: Mesh;
        ceil: Mesh;
        main_wall: Mesh;
        floor: Mesh;
    };
    materials: {
        ["Material.002"]: MeshStandardMaterial;
        ["Material.003"]: MeshStandardMaterial;
        steny: MeshStandardMaterial | MeshLambertMaterial;
        main_wall_material: MeshStandardMaterial | MeshLambertMaterial;
        pol: MeshStandardMaterial;
        potolok: MeshStandardMaterial;
    };
};
//FIXME почему...
const defaultWidth = 6855;
const defaultDepth = 10000;
const tileSize = defaultWidth / 4;
const DEFAULT_WALL_COLOR = "#dddddd";
const GREEN_WALL_COLOR = "#00ff00";

export const ImportedRoom = memo(({ gltfRoom }: { gltfRoom: GLTFResult }) => {
    const group = useRef<Group>(null);
    const roomRef = useRef<any>(null);
    const ceilingRef = useRef<any>(null);
    const lampRef = useRef<any>(null);
    const lampRefGroup = useRef<any>(null);
    const ceilingRefMesh = useRef<any>(null);

    const { nodes, materials } = gltfRoom;
    const isWallsGreen = useStoreSystem((state) => state.isWallsGreen);
    const wallMaterial = useMemo(() => new MeshLambertMaterial(), []);
    const mainWallMaterial = useMemo(() => new MeshLambertMaterial(), []);
    const polMaterial = useMemo(() => new MeshPhysicalMaterial(), []);
    const wallTextureMapRef = useRef(materials.steny.map);
    const mainWallTextureMapRef = useRef(materials.main_wall_material.map ?? materials.steny.map);
    const floorTextureMapRef = useRef(materials.pol.map);
    const currentWallColor = isWallsGreen ? GREEN_WALL_COLOR : DEFAULT_WALL_COLOR;

    wallMaterial.map = isWallsGreen ? null : wallTextureMapRef.current;
    wallMaterial.color = new Color(currentWallColor);
    wallMaterial.needsUpdate = true;

    mainWallMaterial.map = isWallsGreen ? null : mainWallTextureMapRef.current;
    mainWallMaterial.color = new Color(currentWallColor);
    mainWallMaterial.needsUpdate = true;

    polMaterial.map = floorTextureMapRef.current;

    materials.steny = wallMaterial;
    materials.steny.side = FrontSide;
    materials.main_wall_material = mainWallMaterial;
    materials.main_wall_material.side = FrontSide;

    materials.pol = polMaterial;
    materials.pol.side = FrontSide;
    materials.pol.metalness = 0;
    materials.pol.roughness = 1;
    const defaultHeight = DEFAULT_HEIGHT + BOTTOM_OFFSET + TOP_OFFSET;
    let roomHeight = 0;
    let roomLength = 0;
    let roomDepth = 0;

    useFrame((state, delta, frame) => {
        const hideCeiling = !useStoreSystem.getState().showCeiling;
        const lampGroup = lampRefGroup.current;
        const ceilingMesh = ceilingRefMesh.current;
        if (hideCeiling) {
            lampGroup.visible = false;
            ceilingMesh.visible = false;
        } else {
            lampGroup.visible = true;
            ceilingMesh.visible = true;
        }
        if (
            roomHeight !== useStoreRoom.getState().height ||
            roomLength !== useStoreRoom.getState().length ||
            roomDepth !== useStoreRoom.getState().depth
        ) {
            roomHeight = useStoreRoom.getState().height;
            roomLength = useStoreRoom.getState().length;
            roomDepth = useStoreRoom.getState().depth;
            const lampPositions = useStoreRoom.getState().lamp.positions;
            roomRef.current.scale.x = roomLength / defaultWidth;
            ceilingRef.current.scale.x = roomLength / defaultWidth;
            const repeatX = roomLength / tileSize;
            if (materials.main_wall_material.map) {
                materials.main_wall_material.map.repeat.x = repeatX;
            }
            if (materials.pol.map) {
                //FIXME почему 7
                materials.pol.map.repeat.x = (roomLength * 7) / defaultWidth;
            }
            if (materials.potolok.map) {
                materials.potolok.map.repeat.x = roomLength / defaultWidth;
            }

            roomRef.current.scale.y = roomHeight / defaultHeight;
            ceilingRef.current.scale.y = roomHeight / defaultHeight;
            const repeatY = roomHeight / tileSize;
            if (materials.main_wall_material.map) {
                materials.main_wall_material.map.repeat.y = repeatY;
            }
            if (materials.steny.map) {
                materials.steny.map.repeat.y = repeatY;
            }

            roomRef.current.scale.z = roomDepth / defaultDepth;
            ceilingRef.current.scale.z = roomDepth / defaultDepth;
            //FIXME почему 4
            const repeatZ = roomDepth / (defaultDepth / 4);
            if (materials.main_wall_material.map) {
                materials.main_wall_material.map.repeat.y = repeatZ;
            }
            if (materials.pol.map) {
                //FIXME почему 7
                materials.pol.map.repeat.y = (roomDepth * 7) / defaultDepth;
            }
            if (materials.potolok.map) {
                materials.potolok.map.repeat.y = roomDepth / defaultDepth;
            }
            if (materials.potolok && materials.potolok.color.getHexString() !== 'c7c7c7') {
                materials.potolok.color = new Color("#c7c7c7");
            }

            const wallColorHex = currentWallColor.replace("#", "");
            if (materials.steny && materials.steny.color.getHexString() !== wallColorHex) {
                materials.steny.color = new Color(currentWallColor);
            }
            if (materials.main_wall_material && materials.main_wall_material.color.getHexString() !== wallColorHex) {
                materials.main_wall_material.color = new Color(currentWallColor);
            }

            lampGroup.children.length = 0;
            const oneLamp = lampRef.current;
            // const oneLight = lightRef.current;
            const lampSize = Utils.getBoundaryGeometry(oneLamp).size;
            lampPositions.forEach((position, i) => {
                const newLamp = oneLamp.clone();
                newLamp.position.set(position, (roomHeight - defaultHeight) / 1000, 1);
                if (roomDepth <= 5000 && roomDepth > 3000) {
                    newLamp.position.setZ(roomDepth / 2000 - lampSize.z);
                }
                if (roomDepth <= 3000 && roomDepth > 1500) {
                    newLamp.position.setZ(roomDepth / 2000 - lampSize.z / 2);
                    newLamp.scale.z = 0.5;
                }
                if (roomDepth <= 1500) {
                    newLamp.position.setZ(roomDepth / 2000 - lampSize.z / 4);
                    newLamp.scale.z = 0.25;
                }
                lampGroup.add(newLamp);
            });
        }
    });

    useEffect(() => {
        const defaultHeight = DEFAULT_HEIGHT + BOTTOM_OFFSET + TOP_OFFSET;
        roomRef.current.scale.x = useStoreRoom.getState().length / defaultWidth;
        roomRef.current.scale.y = useStoreRoom.getState().height / defaultHeight;
        roomRef.current.scale.z = useStoreRoom.getState().depth / defaultDepth;
        ceilingRef.current.scale.x = useStoreRoom.getState().length / defaultWidth;
        ceilingRef.current.scale.y = useStoreRoom.getState().height / defaultHeight;
        ceilingRef.current.scale.z = useStoreRoom.getState().depth / defaultDepth;




    }, []);

    return (
        <group ref={group} dispose={null} name={"roomGroup"}>
            <group ref={lampRefGroup} scale={[1, 1, 1]} name="lamps">
                <group ref={lampRef} scale={[1, 1, 1]}>
                    <mesh
                        castShadow
                        receiveShadow
                        geometry={nodes.Cube005.geometry}
                        material={materials["Material.002"]}
                    />
                    <mesh
                        castShadow
                        receiveShadow
                        geometry={nodes.Cube005_1.geometry}
                        material={materials["Material.003"]}
                    />
                </group>
            </group>
            <group ref={ceilingRef}>
                <mesh
                    ref={ceilingRefMesh}
                    castShadow
                    receiveShadow
                    geometry={nodes.ceil.geometry}
                    material={materials.potolok}
                    name="ceil"
                />
            </group>
            <group ref={roomRef} name={"room"}>
                <mesh
                    castShadow
                    receiveShadow
                    geometry={nodes.sideWalls.geometry}
                    material={materials.steny}
                    name="sideWalls"
                />
                <mesh
                    castShadow
                    receiveShadow
                    geometry={nodes.main_wall.geometry}
                    material={materials.main_wall_material}
                    name="main_wall"
                />
                <mesh
                    castShadow
                    receiveShadow
                    geometry={nodes.rear_wall.geometry}
                    material={materials.main_wall_material}
                    name="rear_wall"
                />
                <mesh
                    castShadow={false}
                    receiveShadow={false}
                    geometry={nodes.floor.geometry}
                    material={materials.pol}
                    name="floor"
                />
            </group>
        </group>
    );
});
